/**
 * @typedef { import('vue').Vue } Vue
 */

/**
 * Determines if the browser is an Apple device.
 * @return {boolean} true if the browser is an Apple device.
 */
export function isAppleDevice() {
  return /iPhone|iPad|iPod|Mac/.test(navigator.userAgent);
}

/**
 * Returns true if child is a descendant of (or equal to) the parent.  If either
 * child or parent is null, returns false.
 * @param {Node | null} child
 * @param {Node | null} parent
 * @return {boolean}
 */
export function isDescendantOf(child: Node | null, parent: Node | null): boolean {
  // Short-circuit walking up the child hierarchy if not needed.
  if (!parent) return false;
  while (child) {
    if (parent === child) return true;
    child = child.parentNode;
  }
  return false;
}

/**
 * Given an Element in a group, add a CSS class to it and remove the same class
 * from its siblings.
 * @param {Node} button
 * @param {string} [className='active']
 * @return {void}
 */
export function activateOptionInGroup(button: Node, className = 'active') {
  const parent = button.parentNode;
  let child = parent?.firstChild;
  while (child) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      (child as Element).classList.toggle(className, child === button);
    }
    child = child.nextSibling;
  }
}

/**
 * Returns true if the given object is a jQuery instance.
 * @param {object} obj
 * @return {obj is JQuery}
 */
export function isJQuery(obj: object): obj is JQuery {
  return Boolean(obj) && 'jquery' in obj && Boolean(obj.jquery);
}

/**
 * Returns the Vue instance from a DOM Element.
 */
export function vueInstance(obj: object | null) {
  let component: Record<string, any> | null = null;

  if (obj) {
    // Vue attaches internal component references to DOM elements
    const record = obj as Record<string, unknown>;
    component = (record.__vueComponent ?? record.__vueParentComponent ?? null) as typeof component;

    if (component && component.$ && component.$.exposed) {
      component = component.$;
    }
  }

  return component;
}

/**
 * Ever want to do something, not on the upcoming frame, but the next frame
 * after that?  This is a helper to do that.  This is useful, for example, if
 * you want to wait the minimum amount of time that ensures that a paint
 * happened.  You cannot do that with setTimeout().
 */
export function onNextFrame(callback: FrameRequestCallback) {
  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
}

/**
 * Wait for the browser to render before continuing.
 *
 * This is useful to prevent forced layout calculations from blocking the
 * browser, especially when setting up Panzoom.
 */
export function waitForRender() {
  return new Promise((resolve) => {
    onNextFrame(resolve);
  });
}
